/**
 * Cookie Consent Banner Component
 * Displays a banner at the bottom of the page for cookie consent
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Link,
  Paper,
  Slide,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import { havenColors } from '../../theme/theme';

const CookieConsentBanner: React.FC = () => {
  const { isPending, acceptAll, rejectAll } = useCookieConsent();

  // Don't show banner if consent has been given
  if (!isPending) {
    return null;
  }

  return (
    <Slide direction="up" in={isPending} appear={false} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: 'white',
          borderTop: `3px solid ${havenColors.primaryTeal}`,
          borderRadius: 0,
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 0 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 3 },
            }}
          >
            {/* Text Content */}
            <Box sx={{ flex: 1 }}>
              {/* A <p>, not a heading. As an <h2> this was the first heading
                  in document order on every page, so anyone navigating by
                  heading landed on cookie text before the page's own title.
                  The banner is already announced via its dialog role. */}
              <Typography
                variant="h6"
                component="p"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  fontWeight: 600,
                  color: havenColors.darkGray,
                  mb: 0.5,
                }}
              >
                We use cookies
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  color: havenColors.secondaryGrey,
                  lineHeight: 1.6,
                }}
              >
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                By clicking "Accept All", you consent to our use of cookies.{' '}
                <Link
                  component={RouterLink}
                  to="/cookies"
                  sx={{
                    color: havenColors.tealText,
                    textDecoration: 'underline',
                    fontWeight: 500,
                    '&:hover': {
                      color: havenColors.darkTeal,
                    },
                  }}
                >
                  Read our cookie policy
                </Link>
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 1.5 },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: '100%', sm: '280px' },
              }}
            >
              <Button
                variant="outlined"
                onClick={rejectAll}
                sx={{
                  borderColor: havenColors.secondaryGrey,
                  color: havenColors.darkGray,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: havenColors.darkGray,
                    backgroundColor: havenColors.lightGray,
                  },
                }}
              >
                Reject All
              </Button>
              <Button
                variant="contained"
                onClick={acceptAll}
                sx={{
                  background: `linear-gradient(135deg, ${havenColors.primaryTeal} 0%, ${havenColors.lightTeal} 100%)`,
                  color: 'white',
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 14px 0 rgba(0, 196, 193, 0.39)',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${havenColors.darkTeal} 0%, ${havenColors.primaryTeal} 100%)`,
                    boxShadow: '0 6px 20px 0 rgba(0, 196, 193, 0.5)',
                  },
                }}
              >
                Accept All
              </Button>
            </Box>
          </Box>
        </Container>
      </Paper>
    </Slide>
  );
};

export default CookieConsentBanner;
